import { MultiComboBoxInput } from '@components/inputs/ComboBox';
import { Modal } from '@components/modal';
import {
  useFetchAssignableUsers,
  useFetchChange,
  useUpdateAssignment,
  useUpdateStatus,
} from 'api/anomalies/anomalyServices';
import { type Disclosure } from 'interfaces/disclosure/Disclosure.interface';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectStatus } from '../../components/SelectStatus';
import { type DropdownOptions } from '@components/inputs/SelectInput/MultiSelectInput/MultiSelectInput';
import { useDebounce } from 'hooks/useDebounce';
import { useFeatureFlag } from 'context/FeatureFlagProvider/FeatureFlagProvider';
import { FEATURE_FLAGS_LIST } from 'lib/featureFlagsList';
import Skeleton from 'react-loading-skeleton';
import { LoadingSpinner } from '@components/loadingSpinner';

interface UpdateStatusModalProps extends Disclosure {
  changeId?: string;
}

const UpdateStatusModal: FC<UpdateStatusModalProps> = ({
  isOpen,
  handleClose,
  handleOpen,
  changeId,
}) => {
  const [queryUser, setQueryUser] = useState('');
  const queryDebounced = useDebounce(queryUser, 300);
  const { t } = useTranslation();
  const { data, isFetching: isFetchingChange } = useFetchChange(changeId);
  const [statusValue, setStatusValue] = useState<string | number | undefined>();
  const { hasFeatureFlagPermission } = useFeatureFlag();
  const { data: assignableUsers, isFetching: isFetchingAssignableUsers } =
    useFetchAssignableUsers(queryDebounced, changeId);

  const { mutate: updateStatus, isPending: isUpdateStatusLoading } =
    useUpdateStatus(changeId);
  const { mutate: updateAssignment, isPending: isUpdateAssignmentLoading } =
    useUpdateAssignment(changeId);
  const [userResponsible, setUserResponsible] = useState<DropdownOptions>();

  const options = useMemo(() => {
    if (!assignableUsers?.users?.length) return [];
    return assignableUsers.users.map((user) => {
      const fullName = `${user.given_name} ${user.family_name}`;

      return {
        name: fullName,
        value: user.id,
      };
    });
  }, [assignableUsers]);

  const handleUserResponsibleChange = (
    value: DropdownOptions | DropdownOptions[]
  ): void => {
    if (!Array.isArray(value)) {
      setUserResponsible(value);
      const assignedUserId = value?.value ? Number(value.value) : undefined;
      const currentUserId = data?.anomaly?.assigned_to_user?.id;

      if (assignedUserId !== currentUserId) {
        updateAssignment({ assigned_to_user: assignedUserId });
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (data?.anomaly.status) {
        setStatusValue(data.anomaly.status);
      }
      if (data?.anomaly?.assigned_to_user?.id) {
        const currentUser = data.anomaly.assigned_to_user;
        const fullName = `${currentUser.given_name} ${currentUser.family_name}`;
        setUserResponsible({
          value: currentUser.id,
          name: fullName,
        });
      } else {
        setUserResponsible(undefined);
      }
    }
  }, [data, isOpen]);

  const handleStatusChange = (value: string | number | undefined): void => {
    setStatusValue(value);
    if (value && value !== data?.anomaly.status) {
      updateStatus({ status: value });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOpen={handleOpen}
      title={t('anomaliesPage.modal.title')}
      width={550}
      minHeight={450}
      actionBtns={{
        secondary: { label: t('common.buttons.close') },
      }}
    >
      <div className="flex flex-col gap-6">
        <p>{t('anomaliesPage.modal.body')}</p>
        <div className="flex flex-col gap-2">
          <label className="font-bold text-gray-60">
            {t('common.statusLabel')}
          </label>
          {isFetchingChange ? (
            <Skeleton height={'40px'} borderRadius={'8px'} />
          ) : (
            <div className="relative">
              <SelectStatus
                value={statusValue}
                isLoading={isUpdateStatusLoading}
                statusList={data?.available_statuses.statuses}
                onChange={handleStatusChange}
                disabled={
                  !hasFeatureFlagPermission(
                    FEATURE_FLAGS_LIST.changes.patch_status
                  ) || isUpdateStatusLoading
                }
              />
              {isUpdateStatusLoading && (
                <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <LoadingSpinner size={16} />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label className="font-bold  text-gray-60">
            {t('common.responsible')}
          </label>
          <div className="relative">
            <MultiComboBoxInput
              showSearchIcon={false}
              multiple={false}
              isFetching={
                isFetchingAssignableUsers || isUpdateAssignmentLoading
              }
              options={options ?? []}
              placeholder={t('common.placeholders.selectUser')}
              onChange={handleUserResponsibleChange}
              query={queryUser}
              handleQuery={setQueryUser}
              initialValues={userResponsible}
              data-testid="user-select"
              disableSelectedList={true}
              disabled={
                !hasFeatureFlagPermission(
                  FEATURE_FLAGS_LIST.changes.patch_status
                ) || isUpdateAssignmentLoading
              }
            />
            {isUpdateAssignmentLoading && (
              <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <LoadingSpinner size={16} />
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
export default UpdateStatusModal;
